import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface FlexMessage {
  type: string;
  data?: unknown;
  timestamp?: number;
}

export interface FlexConnectionStatus {
  isConnected: boolean;
  lastHeartbeat?: number;
}

@Injectable({
  providedIn: 'root',
})
export class FlexCommunicatorService {
  private readonly ngZone = inject(NgZone);
  private readonly CONNECTION_TIMEOUT = 45000; // 45 seconds (30s heartbeat + 15s buffer)
  private readonly DISCONNECT_DELAY = 5000; // 5 seconds delay before showing disconnected
  private readonly HANDSHAKE_TYPE = 'HANDSHAKE';
  private readonly HEARTBEAT_TYPE = 'HEARTBEAT';
  private readonly RESPONSE_TYPE = 'RESPONSE';
  private readonly FLEX_ORIGIN = 'https://flex.twilio.com'; // Adjust to match your Flex origin

  private heartbeatInterval?: number;
  private connectionCheckInterval?: number;
  private disconnectDelayTimeout?: number;
  private lastParentHeartbeat = Date.now();

  private connectionStatus$ = new BehaviorSubject<FlexConnectionStatus>({
    isConnected: false,
  });

  private messages$ = new Subject<FlexMessage>();

  constructor() {
    console.log('[FlexCommunicator] Service constructor called');
    this.initializePostMessageCommunication();
    this.startConnectionMonitoring();
    this.sendInitialHandshake();
  }

  get isConnected$() {
    return this.connectionStatus$.asObservable();
  }

  get messages() {
    return this.messages$.asObservable();
  }

  get isConnected(): boolean {
    return this.connectionStatus$.value.isConnected;
  }

  private getSourceDescription(source: MessageEventSource | null): string {
    if (!source) return 'null';

    try {
      return source.constructor?.name || 'Window';
    } catch (error) {
      // Cross-origin access blocked - this is expected for security
      return 'CrossOriginWindow';
    }
  }

  private initializePostMessageCommunication(): void {
    console.log('[FlexCommunicator] Initializing PostMessage communication...');

    window.addEventListener('message', (event: MessageEvent) => {
      // Validate origin for security
      if (event.origin !== this.FLEX_ORIGIN) {
        return;
      }

      console.log(
        '✅ [FlexCommunicator] ACCEPTING - Origin matches, processing message:',
        event.data
      );
      this.ngZone.run(() => {
        this.handleParentMessage(event.data);
      });
    });

    console.log('[FlexCommunicator] PostMessage communication initialized');
  }

  private sendInitialHandshake(): void {
    // Wait a bit for parent to be ready, then send initial handshake
    setTimeout(() => {
      console.log('[FlexCommunicator] Sending initial handshake...');
      this.sendHandshake();
    }, 1000);
  }

  private handleParentMessage(message: FlexMessage): void {
    console.log('[FlexCommunicator] Handling parent message:', message);
    this.lastParentHeartbeat = Date.now();
    console.log(
      '[FlexCommunicator] Updated lastParentHeartbeat to:',
      this.lastParentHeartbeat
    );

    // Cancel any pending disconnect since we received a message
    this.cancelDisconnect();

    switch (message.type) {
      case this.HANDSHAKE_TYPE:
        console.log('[FlexCommunicator] Handling handshake message');
        this.handleHandshake(message);
        break;
      case this.HEARTBEAT_TYPE:
        console.log('[FlexCommunicator] Handling heartbeat message');
        this.handleHeartbeat(message);
        break;
      case this.RESPONSE_TYPE:
        console.log('[FlexCommunicator] Handling response message');
        this.handleResponse(message);
        break;
      default:
        console.log(
          '[FlexCommunicator] Handling custom message:',
          message.type
        );
        this.messages$.next(message);
        break;
    }
  }

  private handleHandshake(message: FlexMessage): void {
    console.log('[FlexCommunicator] Handling handshake from parent:', message);
    this.updateConnectionStatus(true);

    const response = {
      type: this.RESPONSE_TYPE,
      data: { handshakeReceived: true, childReady: true },
    };
    console.log('[FlexCommunicator] Sending handshake response:', response);
    this.sendMessage(response);
  }

  private handleHeartbeat(message: FlexMessage): void {
    console.log('[FlexCommunicator] Handling heartbeat from parent:', message);
    this.updateConnectionStatus(true);

    const response = {
      type: this.RESPONSE_TYPE,
      data: { heartbeatReceived: true },
    };
    console.log('[FlexCommunicator] Sending heartbeat response:', response);
    this.sendMessage(response);
  }

  private handleResponse(message: FlexMessage): void {
    console.log(
      '[FlexCommunicator] Received response from parent:',
      message.data
    );
    this.updateConnectionStatus(true);
  }

  private sendHandshake(): void {
    const handshake = {
      type: this.HANDSHAKE_TYPE,
      data: { childReady: true },
    };
    console.log('[FlexCommunicator] Sending initial handshake:', handshake);
    this.sendMessage(handshake);
  }

  private startConnectionMonitoring(): void {
    console.log(
      '[FlexCommunicator] Starting connection monitoring with timeout:',
      this.CONNECTION_TIMEOUT
    );

    this.ngZone.runOutsideAngular(() => {
      this.connectionCheckInterval = window.setInterval(() => {
        this.ngZone.run(() => {
          const now = Date.now();
          const timeSinceLastHeartbeat = now - this.lastParentHeartbeat;

          console.log(
            '[FlexCommunicator] Connection check - Time since last heartbeat:',
            timeSinceLastHeartbeat,
            'ms'
          );

          if (timeSinceLastHeartbeat > this.CONNECTION_TIMEOUT) {
            console.log(
              '[FlexCommunicator] Connection timeout exceeded, scheduling disconnect check...'
            );
            this.scheduleDisconnect();
          }
        });
      }, this.CONNECTION_TIMEOUT / 2);
    });
  }

  private scheduleDisconnect(): void {
    // Clear any existing disconnect timeout
    if (this.disconnectDelayTimeout) {
      clearTimeout(this.disconnectDelayTimeout);
    }

    // Only schedule disconnect if currently connected
    if (this.connectionStatus$.value.isConnected) {
      console.log(
        '[FlexCommunicator] Scheduling disconnect check in',
        this.DISCONNECT_DELAY,
        'ms'
      );

      this.disconnectDelayTimeout = window.setTimeout(() => {
        // Double-check that we're still timed out
        const now = Date.now();
        const timeSinceLastHeartbeat = now - this.lastParentHeartbeat;

        if (timeSinceLastHeartbeat > this.CONNECTION_TIMEOUT) {
          console.log(
            '[FlexCommunicator] Disconnect delay completed, marking as disconnected'
          );
          this.updateConnectionStatus(false);
        } else {
          console.log(
            '[FlexCommunicator] Heartbeat received during disconnect delay, staying connected'
          );
        }

        this.disconnectDelayTimeout = undefined;
      }, this.DISCONNECT_DELAY);
    }
  }

  private cancelDisconnect(): void {
    if (this.disconnectDelayTimeout) {
      console.log('[FlexCommunicator] Cancelling scheduled disconnect');
      clearTimeout(this.disconnectDelayTimeout);
      this.disconnectDelayTimeout = undefined;
    }
  }

  private updateConnectionStatus(isConnected: boolean): void {
    const currentStatus = this.connectionStatus$.value;
    console.log(
      '[FlexCommunicator] Connection status update - Current:',
      currentStatus.isConnected,
      'New:',
      isConnected
    );

    if (currentStatus.isConnected !== isConnected) {
      const newStatus = {
        isConnected,
        lastHeartbeat: isConnected ? Date.now() : currentStatus.lastHeartbeat,
      };
      console.log(
        '[FlexCommunicator] Connection status changed to:',
        newStatus
      );
      this.connectionStatus$.next(newStatus);
    }
  }

  sendMessage(message: FlexMessage): void {
    console.log('📤 [FlexCommunicator] Attempting to send message:', message);

    try {
      const messageWithTimestamp = {
        ...message,
        timestamp: Date.now(),
      };

      // Send to parent window (opener)
      if (window.opener && !window.opener.closed) {
        console.log(
          '📤 [FlexCommunicator] Sending message to parent via opener:',
          {
            target: 'window.opener',
            targetOrigin: this.FLEX_ORIGIN,
            openerExists: Boolean(window.opener),
            openerClosed: window.opener.closed,
            message: messageWithTimestamp,
          }
        );
        window.opener.postMessage(messageWithTimestamp, this.FLEX_ORIGIN);
        console.log(
          '✅ [FlexCommunicator] Message sent successfully to parent via opener'
        );
      } else {
        console.warn(
          '⚠️ [FlexCommunicator] Cannot send message via opener - trying parent instead:',
          {
            openerExists: Boolean(window.opener),
            openerClosed: window.opener?.closed || 'N/A',
            parentExists: Boolean(window.parent),
          }
        );
        window.parent.postMessage(messageWithTimestamp, this.FLEX_ORIGIN);
        console.log(
          '✅ [FlexCommunicator] Message sent to parent via window.parent'
        );
      }
    } catch (error) {
      console.error(
        '❌ [FlexCommunicator] Error sending message to parent:',
        error
      );
    }
  }

  sendOutboundCall(phoneNumber: string, context?: unknown): void {
    console.log(
      '[FlexCommunicator] Sending outbound call request for:',
      phoneNumber,
      'with context:',
      context
    );
    this.sendMessage({
      type: 'OUTBOUND_CALL',
      data: {
        action: 'outbound-call',
        phoneNumber: phoneNumber,
        context: context,
        flexCommunicator: true,
      },
    });
  }

  destroy(): void {
    console.log('[FlexCommunicator] Destroying service...');

    if (this.connectionCheckInterval) {
      console.log('[FlexCommunicator] Clearing connection check interval');
      clearInterval(this.connectionCheckInterval);
    }

    if (this.heartbeatInterval) {
      console.log('[FlexCommunicator] Clearing heartbeat interval');
      clearInterval(this.heartbeatInterval);
    }

    if (this.disconnectDelayTimeout) {
      console.log('[FlexCommunicator] Clearing disconnect delay timeout');
      clearTimeout(this.disconnectDelayTimeout);
    }

    console.log('[FlexCommunicator] Completing observables');
    this.connectionStatus$.complete();
    this.messages$.complete();
    console.log('[FlexCommunicator] Service destroyed');
  }
}
